import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'supporthub-theme';

const readStoredIsDark = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark';
  } catch {
    return false;
  }
};

const applyThemeToDocument = (isDark) => {
  const root = document.documentElement;
  root.dataset.theme = isDark ? 'dark' : 'light';
  root.style.colorScheme = isDark ? 'dark' : 'light';
};

const ThemeContext = createContext({
  isDark: false,
  setDark: () => {},
  toggleDark: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const isEmbed = location.pathname.startsWith('/embed');
  const [isDark, setIsDark] = useState(readStoredIsDark);

  useEffect(() => {
    if (isEmbed) {
      applyThemeToDocument(false);
      return;
    }

    applyThemeToDocument(isDark);
    try {
      localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
    } catch {
      /* ignore quota / private mode */
    }
  }, [isDark, isEmbed]);

  const setDark = useCallback((value) => {
    setIsDark(Boolean(value));
  }, []);

  const toggleDark = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({ isDark, setDark, toggleDark }),
    [isDark, setDark, toggleDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
